import type { FrameRecord, FrameState, MinutePoint } from '@erectus/shared';
import { aggregateSession } from './sessionScore.js';

/**
 * Through-the-session views of one recording: how posture moved minute by
 * minute, and a skeleton every five minutes.
 *
 * Both come from the frame records the recorder already keeps on the device —
 * angles, state and the quantized MediaPipe landmarks. Nothing new is
 * captured: no camera pixels are kept (CLAUDE.md principle 1), the snapshot
 * figure is redrawn from landmarks.
 */
const MINUTE_MS = 60_000;
/** Five minutes since checks became 15–30 min (2026-10-16); ten gave a 15-minute check two pictures. */
export const SNAPSHOT_EVERY_MS = 5 * MINUTE_MS;
/** A snapshot uses the measurable frame closest to its mark, within this. */
const SNAPSHOT_REACH_MS = 90_000;
/** A block shorter than this is too little to score (a session's last minutes). */
const MIN_BLOCK_MS = 2 * MINUTE_MS;

const measurable = (state: FrameState): boolean => state !== 'invalid';
const ratio = (part: number, whole: number): number => Math.round((part / whole) * 100) / 100;

function mean(values: number[], digits = 1): number | null {
  if (values.length === 0) return null;
  const f = 10 ** digits;
  return Math.round((values.reduce((sum, v) => sum + v, 0) / values.length) * f) / f;
}

/** Per-minute means over measurable frames; minutes with no frames are omitted. */
export function minuteSeries(frames: readonly FrameRecord[]): MinutePoint[] {
  const buckets = new Map<number, FrameRecord[]>();
  for (const frame of frames) {
    const m = Math.floor(frame.t / MINUTE_MS);
    const bucket = buckets.get(m);
    if (bucket) bucket.push(frame);
    else buckets.set(m, [frame]);
  }
  return [...buckets.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([m, rows]) => {
      const valid = rows.filter((r) => measurable(r.state));
      const pick = (key: 'cva' | 'trunk' | 'prot'): number[] =>
        valid.map((r) => r[key]).filter((v): v is number => v !== null);
      return {
        m,
        cva: mean(pick('cva')),
        trunk: mean(pick('trunk')),
        // A ratio around 0.1–0.2: one decimal would erase it.
        prot: mean(pick('prot'), 3),
        good: valid.length ? ratio(valid.filter((r) => r.state === 'good').length, valid.length) : 0,
        valid: ratio(valid.length, rows.length),
      };
    });
}

export interface Snapshot {
  /** ms from session start of the frame shown. */
  t: number;
  /** The ten-minute block this snapshot opens. */
  blockStart: number;
  blockEnd: number;
  /** Score of that block, same formula as the session score. */
  blockScore: number;
  /** Share of the block's measurable time in good posture, 0–1. */
  blockGood: number;
  state: FrameState;
  /** Absent on snapshots saved before thresholds v2. */
  cva?: number | null;
  trunk: number | null;
  lm: Int16Array;
}

/**
 * A snapshot at 0, 5, 10 … minutes: the measurable frame closest to each
 * mark, with the score of the five minutes that follow it.
 */
export function snapshotsFrom(frames: readonly FrameRecord[], sampleFps = 3): Snapshot[] {
  if (frames.length === 0) return [];
  const last = frames[frames.length - 1]!.t;
  const out: Snapshot[] = [];
  for (let mark = 0; mark <= last; mark += SNAPSHOT_EVERY_MS) {
    const blockEnd = Math.min(mark + SNAPSHOT_EVERY_MS, last);
    if (blockEnd - mark < MIN_BLOCK_MS && out.length > 0) break;

    let best: FrameRecord | null = null;
    for (const frame of frames) {
      if (!measurable(frame.state) || frame.lm.length === 0) continue;
      const distance = Math.abs(frame.t - mark);
      if (distance > SNAPSHOT_REACH_MS) continue;
      if (!best || distance < Math.abs(best.t - mark)) best = frame;
    }
    if (!best) continue;

    const block = frames.filter((f) => f.t >= mark && f.t < mark + SNAPSHOT_EVERY_MS);
    const aggregate = aggregateSession(block, { sampleFps });
    out.push({
      t: best.t,
      blockStart: mark,
      blockEnd,
      blockScore: aggregate.score,
      blockGood: aggregate.goodRatio,
      state: best.state,
      cva: best.cva,
      trunk: best.trunk,
      lm: best.lm,
    });
  }
  return out;
}
